// Market-data corpus (VRG-461) react-query hooks.
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { marketItemsApi } from 'services'
import type {
  IMarketItem,
  ListMarketItemsParams,
  MarketItemListResponse,
  SetMarketItemReviewPayload,
} from 'services/marketItemsApi'

const LIST_KEY = ['internal', 'market-items']
const DETAIL_KEY = ['internal', 'market-item']

/**
 * Server-paginated list — mirrors `useCompaniesQuery`'s shape (stable params object,
 * `placeholderData` carried across page/sort changes so the table does not flash
 * empty on every click).
 */
export const useMarketItemsQuery = (params: ListMarketItemsParams) => {
  const stableParams = useMemo(
    () => ({
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
      ...(params.sort ? { sort: params.sort } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
    }),
    [params.limit, params.offset, params.sort, params.q, params.status],
  )

  return useQuery<MarketItemListResponse, Error>({
    queryKey: [...LIST_KEY, stableParams],
    queryFn: () => marketItemsApi.list(stableParams),
    // No retry: the expected failure is a 403 (permission not granted to this
    // caller's role), and retrying three times only delays the error state.
    retry: false,
    placeholderData: previous => previous,
    refetchOnWindowFocus: false,
  })
}

/**
 * `retry: false, networkMode: 'always'` — REQUIRED, not stylistic. `QueryProvider`'s
 * `defaultOptions.mutations` sets a global `retry: 1`, unconditional (unlike the query
 * defaults' status-aware retry function, this one retries a 400 exactly like a 500).
 * Confirmed live (VRG-461 verify pass, 2026-08-31): a rejected URL left "Reading page"
 * spinning indefinitely — `mutateAsync`'s promise never settled from the CALLER's
 * perspective while a queued retry sat pending, so `createItem.isPending` stayed true
 * long past the point a human reads the button as hung. Matches the reasoning already
 * written on the product-importer's `useImportProductFromUrl` ("a rejected URL won't
 * succeed on retry") — this hook just didn't apply it.
 */
export const useCreateMarketItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (url: string) => marketItemsApi.createFromUrl(url),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}

export const useSetMarketItemReview = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: SetMarketItemReviewPayload & { id: string }) =>
      marketItemsApi.setReview(id, payload),
    // See useCreateMarketItem above — same global-retry landmine.
    retry: false,
    networkMode: 'always',
    onSuccess: (updated: IMarketItem) => {
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
      queryClient.setQueryData([...DETAIL_KEY, updated.id], updated)
    },
  })
}

/**
 * One item, straight from the API (VRG-537) — the detail page needs to resolve on
 * a direct link or reload, not just whatever the list's cache happens to hold.
 */
export const useMarketItemQuery = (id: string | undefined) =>
  useQuery<IMarketItem, Error>({
    queryKey: [...DETAIL_KEY, id],
    queryFn: () => marketItemsApi.getById(id as string),
    enabled: Boolean(id),
    retry: false,
    refetchOnWindowFocus: false,
  })

/**
 * Problem/solution save (VRG-537 AC1) — same mutation as
 * `useSetMarketItemReview`'s payload shape, kept as its own hook so the detail
 * page's save button doesn't also invalidate on every row-action toggle
 * elsewhere, and so its `retry: false, networkMode: 'always'` is impossible to
 * miss reading this file (§ "every mutation hook in this epic carries it").
 */
export const useUpdateMarketItemOutreach = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      problemStatement,
      solutionStatement,
    }: {
      id: string
      problemStatement: string | null
      solutionStatement: string | null
    }) => marketItemsApi.setReview(id, { problemStatement, solutionStatement }),
    retry: false,
    networkMode: 'always',
    onSuccess: (updated: IMarketItem) => {
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
      queryClient.setQueryData([...DETAIL_KEY, updated.id], updated)
    },
  })
}

/** Replace the full company set for an item (VRG-537 AC2). */
export const useSetMarketItemCompanies = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, companyIds }: { id: string; companyIds: string[] }) =>
      marketItemsApi.setCompanies(id, companyIds),
    retry: false,
    networkMode: 'always',
    onSuccess: (updated: IMarketItem) => {
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
      queryClient.setQueryData([...DETAIL_KEY, updated.id], updated)
    },
  })
}

/**
 * VRG-573 — the four review actions on what extraction proposed: confirm or dismiss a
 * company link, approve or dismiss a pull-quote.
 *
 * One factory rather than four near-identical hooks. Every one has the same shape — take
 * the item id and a child id, return the updated item, invalidate the list and write the
 * detail cache — and four copies of that would be four places to forget `retry: false`
 * (the epic-wide rule: these are not idempotent from the user's point of view, and a
 * silent retry of a dismiss re-runs a delete against a row that is already gone).
 */
const itemChildAction = (fn: (id: string, childId: string) => Promise<IMarketItem>) =>
  function useItemChildAction() {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: ({ id, childId }: { id: string; childId: string }) => fn(id, childId),
      retry: false,
      networkMode: 'always',
      onSuccess: (updated: IMarketItem) => {
        // The Library's Companies column reads the same rows, so its counts go stale the
        // moment one is confirmed — invalidating the list is what keeps the two surfaces
        // telling the same story.
        queryClient.invalidateQueries({ queryKey: LIST_KEY })
        queryClient.setQueryData([...DETAIL_KEY, updated.id], updated)
      },
    })
  }

export const useConfirmMarketItemCompany = itemChildAction((id, linkId) =>
  marketItemsApi.confirmCompany(id, linkId),
)
export const useDismissMarketItemCompany = itemChildAction((id, linkId) =>
  marketItemsApi.dismissCompany(id, linkId),
)
export const useApproveMarketItemQuote = itemChildAction((id, quoteId) =>
  marketItemsApi.approveQuote(id, quoteId),
)
export const useDismissMarketItemQuote = itemChildAction((id, quoteId) =>
  marketItemsApi.dismissQuote(id, quoteId),
)

/**
 * VRG-538 AC1 — set an item's targeting tags.
 *
 * `retry: false` like every mutation in this epic: these are not idempotent from the
 * user's point of view, and a silent retry of a tag write could re-apply a value the user
 * has since cleared.
 */
export const useSetMarketItemTags = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string
      solution?: string | null
      categories?: string[] | null
      targetRoles?: string[] | null
    }) => marketItemsApi.setTags(id, payload),
    retry: false,
    networkMode: 'always',
    onSuccess: (updated: IMarketItem) => {
      // The Library filters read these columns, so a tag edit invalidates the list too.
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
      queryClient.setQueryData([...DETAIL_KEY, updated.id], updated)
    },
  })
}
